import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  notificationsApi,
  NotificationListItem,
  NotificationStatus,
  NotificationType,
  ListNotificationsParams,
  ArchivedFilter,
  SourceFilter,
} from '../../api/notifications';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Search, ChartColumn, X, Archive, ArchiveRestore, Trash2, Bot, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from '../../components/shared/ConfirmDialog';
import { getErrorMessage } from '@/lib/errors';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { Pagination } from '@/components/shared/Pagination';
import { EmptyState, ErrorState, FilterChips, FormField, PageHeader, StatusPill, Toolbar, type FilterChip } from '@/components/app';
import { NOTIFICATION_STATUS_LABELS, NOTIFICATION_TYPE_LABELS, labelOf } from '@/lib/labels';
import { notificationStatusTone } from '@/lib/status';
import { formatTashkentShort } from '@/lib/datetime';

const ITEMS_PER_PAGE = 20;
const fmt = (n: number) => n.toLocaleString('ru-RU');

/**
 * Человеческие названия триггерных кампаний. Метка приходит с бэкенда как
 * машинный ключ из `data.campaign`; незнакомый ключ показываем как есть, чтобы
 * новая кампания не оставалась безымянной до правки админки.
 */
const CAMPAIGN_LABELS: Record<string, string> = {
  abandoned_order: 'Незавершённая оплата',
};

const ARCHIVED_LABELS: Record<ArchivedFilter, string> = {
  all: 'Все уведомления',
  active: 'Видны в приложении',
  archived: 'Скрытые',
};

const SOURCE_LABELS: Record<SourceFilter, string> = {
  all: 'Любой источник',
  manual: 'Созданные вручную',
  auto: 'Автоматические',
};

const DEFAULT_FILTERS: ListNotificationsParams = { page: 1, limit: ITEMS_PER_PAGE, archived: 'all', source: 'all' };

export default function NotificationsPage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [notifications, setNotifications] = useState<NotificationListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<ListNotificationsParams>(DEFAULT_FILTERS);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);

  // Любая смена фильтра возвращает на первую страницу: иначе запрос уходил за
  // текущей страницей уже отфильтрованного списка и мог вернуть пустоту.
  const updateFilters = (patch: Partial<ListNotificationsParams>) => {
    setFilters((prev) => ({ ...prev, ...patch, page: 1 }));
    setCurrentPage(1);
  };
  const applySearch = useDebouncedCallback((value: string) => updateFilters({ search: value || undefined }));

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchInput('');
    setCurrentPage(1);
  };

  const fetchNotifications = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await notificationsApi.list({ ...filters, page: currentPage });
      setNotifications(data.notifications);
      setTotalPages(data.pagination.total_pages);
      setTotal(data.pagination.total);
    } catch (error: unknown) {
      setLoadError(getErrorMessage(error, 'Не удалось загрузить уведомления'));
    } finally {
      setIsLoading(false);
    }
  }, [filters, currentPage]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const handleCancel = async (id: string) => {
    const ok = await confirm({
      title: 'Отменить запланированное уведомление?',
      description: 'Уведомление не будет отправлено.',
      confirmLabel: 'Отменить рассылку',
      destructive: true,
    });
    if (!ok) return;
    try {
      await notificationsApi.cancel(id);
      toast.success('Уведомление отменено');
      fetchNotifications();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Не удалось отменить уведомление'));
    }
  };

  const handleArchive = async (notification: NotificationListItem) => {
    const ok = await confirm({
      title: 'Скрыть уведомление из приложения?',
      description:
        'Пользователи перестанут его видеть, счётчик непрочитанных пересчитается. ' +
        'Статистика доставки и прочтений сохранится, вернуть можно в любой момент.',
      confirmLabel: 'Скрыть',
    });
    if (!ok) return;
    setBusyId(notification.id);
    try {
      await notificationsApi.archive(notification.id);
      toast.success('Уведомление скрыто из приложения');
      fetchNotifications();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Не удалось скрыть уведомление'));
    } finally {
      setBusyId(null);
    }
  };

  const handleUnarchive = async (notification: NotificationListItem) => {
    setBusyId(notification.id);
    try {
      await notificationsApi.unarchive(notification.id);
      toast.success('Уведомление снова видно пользователям');
      fetchNotifications();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Не удалось вернуть уведомление'));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (notification: NotificationListItem) => {
    // Отдельно предупреждаем про статистику: у отправленной рассылки это
    // единственный след того, что произошло, и восстановить его нечем.
    const wasDelivered = notification.recipients_count > 0;
    const ok = await confirm({
      title: 'Удалить уведомление навсегда?',
      description: wasDelivered
        ? `Вместе с уведомлением исчезнут получатели (${fmt(notification.recipients_count)}), ` +
          'попытки доставки и вся статистика прочтений. Отменить это будет нельзя. ' +
          'Если нужно только убрать сообщение у пользователей — используйте «Скрыть».'
        : 'Уведомление будет удалено безвозвратно.',
      confirmLabel: 'Удалить навсегда',
      destructive: true,
    });
    if (!ok) return;
    setBusyId(notification.id);
    try {
      await notificationsApi.delete(notification.id);
      toast.success('Уведомление удалено');
      fetchNotifications();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Не удалось удалить уведомление'));
    } finally {
      setBusyId(null);
    }
  };

  const chips: FilterChip[] = [];
  if (filters.search) {
    chips.push({ key: 'search', label: `Поиск: ${filters.search}`, onRemove: () => { setSearchInput(''); updateFilters({ search: undefined }); } });
  }
  if (filters.status) {
    chips.push({ key: 'status', label: `Статус: ${labelOf(NOTIFICATION_STATUS_LABELS, filters.status)}`, onRemove: () => updateFilters({ status: undefined }) });
  }
  if (filters.type) {
    chips.push({ key: 'type', label: `Тип: ${labelOf(NOTIFICATION_TYPE_LABELS, filters.type)}`, onRemove: () => updateFilters({ type: undefined }) });
  }
  if (filters.archived && filters.archived !== 'all') {
    chips.push({ key: 'archived', label: ARCHIVED_LABELS[filters.archived], onRemove: () => updateFilters({ archived: 'all' }) });
  }
  if (filters.source && filters.source !== 'all') {
    chips.push({ key: 'source', label: SOURCE_LABELS[filters.source], onRemove: () => updateFilters({ source: 'all' }) });
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Уведомления"
        subtitle="Сообщения в приложении и push-рассылки"
        actions={
          <Button onClick={() => navigate('/notifications/create')}>
            <Plus aria-hidden />
            Создать уведомление
          </Button>
        }
      />

      <Card className="overflow-hidden">
        <div className="grid grid-cols-1 gap-3 border-b border-border-soft px-[18px] py-4 md:grid-cols-2 xl:grid-cols-5">
          <FormField label="Поиск" tone="filter">
            {(p) => (
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-3" aria-hidden />
                <Input
                  {...p}
                  type="search"
                  placeholder="Заголовок или текст"
                  value={searchInput}
                  onChange={(e) => { setSearchInput(e.target.value); applySearch(e.target.value); }}
                  className="pl-9"
                />
              </div>
            )}
          </FormField>
          <FormField label="Статус" tone="filter">
            {(p) => (
              <Select value={filters.status || 'all'} onValueChange={(value) => updateFilters({ status: value === 'all' ? undefined : (value as NotificationStatus) })}>
                <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все статусы</SelectItem>
                  {Object.values(NotificationStatus).map((s) => (
                    <SelectItem key={s} value={s}>{NOTIFICATION_STATUS_LABELS[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>
          <FormField label="Тип" tone="filter">
            {(p) => (
              <Select value={filters.type || 'all'} onValueChange={(value) => updateFilters({ type: value === 'all' ? undefined : (value as NotificationType) })}>
                <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все типы</SelectItem>
                  {Object.values(NotificationType).map((t) => (
                    <SelectItem key={t} value={t}>{NOTIFICATION_TYPE_LABELS[t]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>
          <FormField label="Видимость" tone="filter">
            {(p) => (
              <Select value={filters.archived || 'all'} onValueChange={(value) => updateFilters({ archived: value as ArchivedFilter })}>
                <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(ARCHIVED_LABELS) as ArchivedFilter[]).map((k) => (
                    <SelectItem key={k} value={k}>{ARCHIVED_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>
          <FormField label="Источник" tone="filter">
            {(p) => (
              <Select value={filters.source || 'all'} onValueChange={(value) => updateFilters({ source: value as SourceFilter })}>
                <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(SOURCE_LABELS) as SourceFilter[]).map((k) => (
                    <SelectItem key={k} value={k}>{SOURCE_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>
        </div>

        <Toolbar title={isLoading ? 'Загрузка…' : `Найдено ${fmt(total)}`}>
          <FilterChips chips={chips} onReset={resetFilters} />
        </Toolbar>

        {loadError ? (
          <ErrorState message={loadError} onRetry={fetchNotifications} className="border-t border-border-soft" />
        ) : !isLoading && notifications.length === 0 ? (
          <EmptyState
            icon={<BellOff />}
            title="Уведомления не найдены"
            description={chips.length ? 'Попробуйте изменить или сбросить фильтры' : undefined}
            action={chips.length ? <Button variant="secondary" onClick={resetFilters}>Сбросить фильтры</Button> : undefined}
            className="border-t border-border-soft"
          />
        ) : (
          <>
            <Table className="min-w-[1080px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Заголовок</TableHead>
                  <TableHead>Тип</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead className="text-right">Прогноз</TableHead>
                  <TableHead className="text-right">Адресовано / push</TableHead>
                  <TableHead className="text-right">Прочитали</TableHead>
                  <TableHead>Создано</TableHead>
                  <TableHead className="text-right">Действия</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading
                  ? Array.from({ length: 6 }, (_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 8 }, (__, j) => (
                          <TableCell key={j}><Skeleton className="h-4 w-full max-w-28" /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  : notifications.map((notification) => (
                      <TableRow key={notification.id}>
                        <TableCell className="max-w-[340px]">
                          <div className="font-semibold text-text">{notification.title}</div>
                          <div className="mt-0.5 truncate text-[13px] text-text-2">{notification.body}</div>
                          {notification.campaign && (
                            <StatusPill size="sm" tone="info" icon={<Bot className="size-3" aria-hidden />} className="mt-1.5">
                              {CAMPAIGN_LABELS[notification.campaign] || notification.campaign}
                              {notification.campaign_period ? ` · ${notification.campaign_period}` : ''}
                            </StatusPill>
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusPill size="sm">{labelOf(NOTIFICATION_TYPE_LABELS, notification.type)}</StatusPill>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col items-start gap-1">
                            <StatusPill tone={notificationStatusTone(notification.status)}>
                              {labelOf(NOTIFICATION_STATUS_LABELS, notification.status)}
                            </StatusPill>
                            {notification.is_archived && (
                              <StatusPill size="sm" icon={<Archive className="size-3" aria-hidden />}>Скрыто</StatusPill>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-text-2">
                          {notification.estimated_recipients != null ? fmt(notification.estimated_recipients) : '—'}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-right tabular-nums">
                          {fmt(notification.recipients_count)} / {fmt(notification.push_sent_count)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{notification.read_rate.toFixed(1)}%</TableCell>
                        <TableCell className="whitespace-nowrap tabular-nums text-text-2">{formatTashkentShort(notification.created_at)}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon-sm" aria-label="Аналитика уведомления" title="Аналитика"
                              onClick={() => navigate(`/notifications/${notification.id}/analytics`)}>
                              <ChartColumn />
                            </Button>
                            {notification.status === NotificationStatus.SCHEDULED && (
                              <Button variant="ghost" size="icon-sm" aria-label="Отменить рассылку" title="Отменить рассылку"
                                onClick={() => handleCancel(notification.id)}>
                                <X />
                              </Button>
                            )}
                            {notification.is_archived ? (
                              <Button variant="ghost" size="icon-sm" aria-label="Вернуть в приложение" title="Вернуть в приложение"
                                disabled={busyId === notification.id} onClick={() => handleUnarchive(notification)}>
                                <ArchiveRestore />
                              </Button>
                            ) : (
                              <Button variant="ghost" size="icon-sm" aria-label="Скрыть из приложения" title="Скрыть из приложения"
                                disabled={busyId === notification.id} onClick={() => handleArchive(notification)}>
                                <Archive />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon-sm" aria-label="Удалить навсегда" title="Удалить навсегда"
                              disabled={busyId === notification.id} onClick={() => handleDelete(notification)}
                              className="text-neg hover:bg-neg-soft">
                              <Trash2 />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>

            {!isLoading && (
              <Pagination currentPage={currentPage} totalPages={totalPages} total={total} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setCurrentPage} />
            )}
          </>
        )}
      </Card>
    </div>
  );
}
